// src/modules/operaciones/inventarios/hooks/useAjustesFiltrados.js
//
// Filtros, orden y paginación de la lista de ajustes.
import { useState, useMemo, useEffect, useCallback } from "react"; // Hooks usados
import { aISO, haceDias, isoDia, comparar } from "../../../../utils/format"; // Fechas y orden compartidos
import { POR_PAGINA, txt, ORDEN, ESTADOS } from "../utils/constantes"; // Constantes y definiciones de la tabla

// Filtros, orden y paginación de la lista de ajustes.
// ⚠️ `ajustes` debe ser una referencia estable entre renders (p. ej. un useState); si el padre lo recrea
//    en cada render, los useMemo internos se recalculan siempre.
export default function useAjustesFiltrados(ajustes) {
  // Estado de filtros con valores iniciales (últimos 30 días)
  const [filtros, setFiltros] = useState(() => ({
    busqueda: "", // Texto de búsqueda
    estado: "todos", // Filtro de estado
    bodega: "", // Filtro de bodega
    concepto: "", // Filtro de concepto
    inicio: aISO(haceDias(30)), // Fecha inicial: hace 30 días
    fin: aISO(new Date()), // Fecha final: hoy
  }));
  const [orden, setOrden] = useState({ key: "fecha", dir: "desc" }); // Orden inicial: fecha descendente
  const [pagina, setPagina] = useState(1); // Página actual
  const { busqueda, estado, bodega, concepto, inicio, fin } = filtros; // Desestructura los filtros

  const actualizar = useCallback((cambios) => setFiltros((f) => ({ ...f, ...cambios })), []); // Mezcla cambios parciales en los filtros
  const limpiar = () => actualizar({ busqueda: "", estado: "todos", bodega: "", concepto: "" }); // Limpia filtros (conserva fechas)
  const hayFiltros =Boolean(busqueda) || estado !== "todos" || Boolean(bodega) || Boolean(concepto); // true si hay algún filtro activo
  // Cambia el orden: alterna asc/desc en la misma columna o inicia asc en otra
  const cambiarOrden = (key) =>
    setOrden((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  useEffect(() => {
    setPagina(1); // Vuelve a la primera página al cambiar cualquier filtro
  }, [busqueda, estado, bodega, concepto, inicio, fin]);

  // Lista de bodegas únicas ordenadas para el selector
  const bodegas = useMemo(
    () => [...new Set(ajustes.map((a) => txt(a.BODE_COD)).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es", { numeric: true })),
    [ajustes]
  );

  // Lista de conceptos únicos (sin distinguir mayúsculas) ordenados
  const conceptos = useMemo(() => {
    const unicos = new Map(); // Mapa minúscula → texto original
    ajustes.forEach((a) => { // Recorre cada ajuste
      const c = txt(a.AJUS_CONC); // Concepto limpio
      if (c && !unicos.has(c.toLowerCase())) unicos.set(c.toLowerCase(), c); // Guarda la primera variante
    });
    return [...unicos.values()].sort((a, b) => a.localeCompare(b, "es")); // Devuelve ordenados alfabéticamente
  }, [ajustes]);

  // Aplica búsqueda, filtros, rango de fechas y orden
  const filtrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase(); // Término de búsqueda normalizado
    const conceptoNorm = concepto.trim().toLowerCase(); // Concepto normalizado
    const { pasa } = ESTADOS[estado] || ESTADOS.todos; // Función de estado, por defecto "todos"
    const lista = ajustes.filter((a) => { // Filtra los ajustes
      if (term) { // Si hay búsqueda
        const pajar = [a.AJUS_ID, a.PREF_PRE, a.BODE_COD, a.AJUS_NUMERO, a.AJUS_CONC, a.AJUS_USUARIO].map(txt).join(" ").toLowerCase(); // Texto donde buscar
        if (!pajar.includes(term)) return false; // Descarta si no contiene el término
      }
      if (bodega && txt(a.BODE_COD) !== bodega) return false; // Descarta si no coincide la bodega
      if (conceptoNorm && txt(a.AJUS_CONC).toLowerCase() !== conceptoNorm) return false; // Descarta si no coincide el concepto
      if (!pasa(a)) return false; // Descarta si no pasa el filtro de estado
      if (inicio || fin) { // Si hay rango de fechas
        const dia = isoDia(a.AJUS_FECHA); // Día ISO del ajuste
        if (!dia || (inicio && dia < inicio) || (fin && dia > fin)) return false; // Descarta si está fuera del rango
      }
      return true; // Pasa todos los filtros
    });
    const valor = ORDEN[orden.key]; // Extractor de valor según la columna ordenada
    const signo = orden.dir === "asc" ? 1 : -1; // Signo según dirección
    return [...lista].sort((a, b) => signo * comparar(valor(a), valor(b))); // Devuelve copia ordenada
  }, [ajustes, busqueda, estado, bodega, concepto, inicio, fin, orden]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA)); // Total de páginas (mínimo 1)
  const paginaActual = Math.min(pagina, totalPaginas); // Página actual limitada al total
  const desde = (paginaActual - 1) * POR_PAGINA; // Índice inicial de la página

  // Chips que muestran los filtros activos y permiten quitarlos
  const chips = useMemo(() => {
    const lista = []; // Lista de chips
    if (bodega) lista.push({ key: "bodega", label: `Bodega: ${bodega}`, onRemove: () => actualizar({ bodega: "" }) }); // Chip de bodega
    if (concepto) lista.push({ key: "concepto", label: `Concepto: ${concepto}`, onRemove: () => actualizar({ concepto: "" }) }); // Chip de concepto
    if (estado !== "todos") { // Chip de estado si no es "todos"
      lista.push({ key: "estado", label: `Estado: ${ESTADOS[estado]?.label || estado}`, onRemove: () => actualizar({ estado: "todos" }) }); // Agrega el chip
    }
    return lista; // Devuelve los chips activos
  }, [bodega, concepto, estado, actualizar]);

  // Valores y funciones que expone el hook
  return {
    filtros, actualizar, limpiar, hayFiltros, orden, cambiarOrden, bodegas, conceptos, filtrados, chips,
    visibles: filtrados.slice(desde, desde + POR_PAGINA), // Filas de la página actual
    paginacion: { paginaActual, totalPaginas, desde, setPagina }, // Datos de paginación
  };
}